import React from "react";
import { Button } from "ui/components";
import { PlayerButtonType } from "core/models";
import $ from "./PlayerButtons.module.scss";

interface Props {
    onButtonClick(button: PlayerButtonType): void;
    playing: boolean;
    trimButtons?: boolean;
    useShortcuts: boolean;
    trimButtonsDisabled?: boolean;
    essentialsOnly?: boolean;
}

const PlayerButtons: React.FC<Props> = ({
    onButtonClick,
    playing,
    trimButtons = false,
    useShortcuts = true,
    trimButtonsDisabled = false,
    essentialsOnly = false,
}: Props) => {
    const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.code) {
            case "ArrowRight":
                if (essentialsOnly) {
                    return;
                }
                onButtonClick("moveFrameForward");
                break;
            case "ArrowLeft":
                if (essentialsOnly) {
                    return;
                }
                onButtonClick("moveFrameBackward");
                break;
            case "KeyK":
                if (essentialsOnly) {
                    return;
                }
                if (event.shiftKey) {
                    onButtonClick("playLast2Seconds");
                }
                break;
            case "Space":
                event.preventDefault();
                if (event.shiftKey) {
                    onButtonClick("playFromStart");
                } else {
                    onButtonClick("togglePlay");
                }
                break;
            case "KeyI":
                if (event.shiftKey) {
                    onButtonClick("goToInPoint");
                } else {
                    onButtonClick("setInPoint");
                }
                break;
            case "KeyO":
                if (event.shiftKey) {
                    onButtonClick("goToOutPoint");
                } else {
                    onButtonClick("setOutPoint");
                }
                break;
            default:
                break;
        }
    };
    React.useEffect(() => {
        if (!useShortcuts) {
            return undefined;
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    });

    return (
        <div className={$.buttonbar}>
            {essentialsOnly && (
                <>
                    <Button
                        type="function"
                        theme="light"
                        icon={!playing ? "play" : "pause"}
                        tooltip={`Play-pause ${useShortcuts ? "(space)" : ""}`}
                        onClick={() => onButtonClick("togglePlay")}
                        className={$.playercontrols_button}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="replay"
                        tooltip={`Play from start ${
                            useShortcuts ? "(⇧ + space)" : ""
                        }`}
                        onClick={() => onButtonClick("playFromStart")}
                        className={$.playercontrols_button}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="frame-backward"
                        tooltip={`Step backward 1 frame ${
                            useShortcuts ? "(left)" : ""
                        }`}
                        onClick={() => onButtonClick("moveFrameBackward")}
                        className={$.playercontrols_button}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="frame-forward"
                        tooltip={`Step forward 1 frame ${
                            useShortcuts ? "(right)" : ""
                        }`}
                        onClick={() => onButtonClick("moveFrameForward")}
                        className={$.playercontrols_button}
                    />
                    <Button
                        className={$.player__controls__button}
                        type="function"
                        theme="light"
                        tooltip={`Mark as in ${useShortcuts ? "(I)" : ""}`}
                        onClick={() => onButtonClick("setInPoint")}
                        icon="mark-in"
                        disabled={trimButtonsDisabled}
                    />
                    <Button
                        className={$.player__controls__button}
                        type="function"
                        theme="light"
                        tooltip={`Mark as out ${useShortcuts ? "(O)" : ""}`}
                        onClick={() => onButtonClick("setOutPoint")}
                        icon="mark-out"
                        disabled={trimButtonsDisabled}
                    />
                </>
            )}
            {!essentialsOnly && (
                <>
                    {trimButtons && (
                        <>
                            <Button
                                className={$.player__controls__button}
                                type="function"
                                theme="light"
                                tooltip={`Trim begin ${
                                    useShortcuts ? "(I)" : ""
                                }`}
                                onClick={() => onButtonClick("setInPoint")}
                                icon="mark-in"
                                disabled={trimButtonsDisabled}
                            />
                            <Button
                                className={$.player__controls__button}
                                type="function"
                                theme="light"
                                tooltip={`Trim end ${
                                    useShortcuts ? "(O)" : ""
                                }`}
                                onClick={() => onButtonClick("setOutPoint")}
                                icon="mark-out"
                                disabled={trimButtonsDisabled}
                            />
                        </>
                    )}

                    <Button
                        type="function"
                        theme="light"
                        icon="replay"
                        tooltip={`Play from start ${
                            useShortcuts ? "(⇧ + space)" : ""
                        }`}
                        onClick={() => onButtonClick("playFromStart")}
                        className={$.playercontrols_button}
                    />
                    <div className={$.spacer} />
                    <Button
                        type="function"
                        theme="light"
                        icon="go-mark-in"
                        tooltip={`Go to in ${useShortcuts ? "(⇧ + I)" : ""}`}
                        onClick={() => onButtonClick("goToInPoint")}
                        className={$.playercontrols_button}
                        disabled={trimButtonsDisabled}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="frame-backward"
                        tooltip={`Step backward 1 frame ${
                            useShortcuts ? "(left)" : ""
                        }`}
                        onClick={() => onButtonClick("moveFrameBackward")}
                        className={$.playercontrols_button}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon={!playing ? "play" : "pause"}
                        tooltip={`Play-pause ${useShortcuts ? "(space)" : ""}`}
                        onClick={() => onButtonClick("togglePlay")}
                        className={$.playercontrols_button}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="frame-forward"
                        tooltip={`Step forward 1 frame ${
                            useShortcuts ? "(right)" : ""
                        }`}
                        onClick={() => onButtonClick("moveFrameForward")}
                        className={$.playercontrols_button}
                    />

                    <Button
                        type="function"
                        theme="light"
                        icon="go-mark-out"
                        tooltip={`Go to out ${useShortcuts ? "(⇧ + O)" : ""}`}
                        onClick={() => onButtonClick("goToOutPoint")}
                        className={$.playercontrols_button}
                        disabled={trimButtonsDisabled}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="play-last-seconds"
                        tooltip={`Play last 2 seconds ${
                            useShortcuts ? "(⇧ + K)" : ""
                        }`}
                        onClick={() => onButtonClick("playLast2Seconds")}
                        className={$.playercontrols_button}
                    />
                </>
            )}
        </div>
    );
};

export default PlayerButtons;
